import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  LinkAuthenticationElement,
  useStripe,
  useElements,
  AddressElement,
} from "@stripe/react-stripe-js";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { call } from "services/axiosService/core";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
// import profilesListData from "layouts/pages/profile/profile-overview/data/profilesListData";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function BulkCheckoutForm({
  learnerDetails,
  amountToProcess,
  averageCost,
  subscriptionType,
  setStep,
  getChildrens,
  updateUserProfile,
  setOpenSnackbar,
  setAlertMessage,
  setSeverity,
}) {
  // const [dispatch] = useMaterialUIController();

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const { t } = useTranslation();

  useEffect(() => {
    if (!stripe) {
      return;
    }
    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const paymentRedirectDetails = {
      learnerDetails: learnerDetails,
      amountToProcess: amountToProcess,
      subscriptionType: subscriptionType,
      checkoutProcess: "bulk",
    };

    try {
      localStorage.setItem("payment_redirect_details", JSON.stringify(paymentRedirectDetails));
    } catch (error) {
      setMessage("An unexpected error has occurred");
      setIsLoading(false);
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment_redirect`,
      },
      redirect: "if_required",
    });
    if (error) {
      setMessage(error.message);
      updatePaymentStatus("fail");
    } else if (paymentIntent) {
      switch (paymentIntent.status) {
        case "succeeded":
          updatePaymentStatus("success");
          setMessage("Payment success");
          break;
        case "canceled":
          updatePaymentStatus("fail");
          setMessage("Payment failed");
          break;
        case "failed":
          updatePaymentStatus("fail");
          setMessage("Payment failed");
          break;
        case "processing":
          navigate(
            "/payment_redirect?payment_intent=" +
              paymentIntent.id +
              "&payment_intent_client_secret=" +
              paymentIntent.client_secret +
              "&redirect_status=" +
              paymentIntent.status
          );
          break;
        case "requires_payment_method":
          setMessage("Payment failed. Please try another card or method.");
          updatePaymentStatus("fail");
          break;
        default:
          updatePaymentStatus("fail");
          setMessage("An unexpected error occurred.");
          break;
      }
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs",
  };

  const LinkAuthenticationElementOptions = {
    defaultValues: { email: profile.email },
  };
  const AddressElementOptions = {
    mode: "billing",
  };

  const updatePaymentStatus = (status) => {
    if (status == "success") {
      // Update the coupon if used - Descrease coupon remaing usage count and add user email adress to coupon uses object
      // Upate coupon if coupon applied while payment
      setStep(10);
      setOpenSnackbar(true);
      setAlertMessage("Payment Success");
      setSeverity("success");
      const usages = learnerDetails
        .filter((learner) => learner.code_status === "valid_code" && learner.referral_code)
        .map((learner) => ({
          referral_code: learner.referral_code,
          email: learner.email,
        }));

      const learner_ids = learnerDetails.map((learner) => learner.learner_id);

      if (usages.length > 0) {
        call(`/payments/update-bulk-coupon-usage`, "PUT", {
          body: {
            usages: usages,
          },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }

      call(`users/${profile.id}/emails`, "POST", {
        body: {
          email_type: "payment_success_email",
          recipient_id: profile.id,
          amount_paid: amountToProcess,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("Email Success");
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });

      call(`/users/bulk-upload/update-status`, "POST", {
        body: {
          learner_details: learnerDetails,
          guardian_id: profile.id,
          payment_status: "success",
          paid_at: new Date(),
          subscription_type: subscriptionType,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("update Success");
            getChildrens();
            setStep(8);
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });

      const AdultDetails = profile;
      let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
      let true_subs = ["true_mindsets_yearly", "true_mindsets_monthly"];

      if (true_subs.includes(subscriptionType)) {
        true_users = Array.from(new Set([...true_users, ...learner_ids]));
        AdultDetails.true_mindsets_users = true_users;
        call(`users/${profile.id}`, "PUT", {
          body: AdultDetails,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
              updateUserProfile();
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }
    } else {
      setOpenSnackbar(true);
      setAlertMessage("Payment Failed");
      setSeverity("error");
      call(`/users/bulk-upload/update-status`, "POST", {
        body: {
          learner_details: learnerDetails,
          guardian_id: profile.id,
          payment_status: "fail",
          paid_at: null,
          subscription_type: subscriptionType,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("update Success");
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <LinkAuthenticationElement
        id="link-authentication-element"
        options={LinkAuthenticationElementOptions}
        onChange={(e) => setEmail(e.value.email)}
      />
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <Typography
        sx={{ display: "flex", alignItems: "center" }}
        variant="h6"
        textAlign="left"
        m={2}
      >
        <span style={{ color: "red", fontSize: "small", paddingRight: "5px" }}>*</span>
        {t("headings.billingAddress")}
        <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>Required</span>
      </Typography>
      <AddressElement id="adress-element" options={AddressElementOptions} />
      <MDBox px={2} py={2} display="flex" justifyContent="end">
        <MDButton
          disabled={isLoading || !stripe || !elements}
          type="submit"
          id="submit"
          style={{
            color: "white",
            background: "#10B0D5",
            marginTop: "15px",
          }}
        >
          <span id="button-text">
            {isLoading ? t("buttonTitles.Processing") : t("buttonTitles.payNow")}
          </span>
        </MDButton>
      </MDBox>

      {/* Show any error or success messages */}
      {message && (
        <div
          style={{
            color: "rgb(105, 115, 134)",
            fontSize: "16px",
            lineHeight: "20px",
            paddingTop: "12px",
            textAlign: "center",
          }}
          id="payment-message"
        >
          {message}
        </div>
      )}
    </form>
  );
}

BulkCheckoutForm.defaultProps = {
  learnerDetails: [],
  amountToProcess: 1,
  averageCost: 1,
  subscriptionType: "maxu_yearly",
  setStep: () => {},
  getChildrens: () => {},
};

BulkCheckoutForm.propTypes = {
  learnerDetails: PropTypes.array,
  amountToProcess: PropTypes.number,
  averageCost: PropTypes.number,
  subscriptionType: PropTypes.string,
  setStep: PropTypes.func,
  getChildrens: PropTypes.func,
};
